import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";

import { OnboardingBrandStep } from "../components/onboarding/OnboardingBrandStep";
import { OnboardingGmailStep } from "../components/onboarding/OnboardingGmailStep";
import { OnboardingReplyStyleStep } from "../components/onboarding/OnboardingReplyStyleStep";
import { OnboardingShell } from "../components/onboarding/OnboardingShell";
import { brandHasPitchContent, brandOnboardingDetailsComplete } from "../components/onboarding/brandUtils";
import { MAILBOX_JUST_CONNECTED_KEY } from "../components/onboarding/constants";
import Button from "../components/ui/Button";
import { brandProfileService } from "../services/brandProfile";
import { mailboxService, mailboxesQueryKey } from "../services/mailbox";
import { userProfileQueryKey, userProfileService } from "../services/userProfile";
import { getApiErrorMessage } from "../utils/apiError";
import { defaultMailbox } from "../utils/mailboxScope";
import { persistMailboxHash } from "../utils/mailboxScopeStorage";
import { mailboxOAuthErrorIsAccountInUse, mailboxOAuthErrorMessage } from "../utils/mailboxOAuthError";
import {
  ONBOARDING_MAILBOX_RETURN_PATH,
  ONBOARDING_FIRST_PATH,
  ONBOARDING_STEP_SLUGS,
  onboardingPathForStep,
  onboardingStepFromSlug,
} from "../config/onboardingRoutes";
import {
  EMAIL_RETENTION_COLD_INBOUND_ONLY,
  normalizeEmailRetentionValue,
} from "../config/emailRetentionOptions";
import { normalizeToneValue } from "../config/toneOptions";
import { trackEvent } from "../utils/tracking";

function emptyBrandForm() {
  return {
    company_name: "",
    website_url: "",
    core_offering: "",
    icp: "",
    custom_context: "",
    brand_context: {},
  };
}

function newLocalDraft() {
  return {
    clientId: `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    serverHash: undefined,
    form: emptyBrandForm(),
  };
}

function brandRowToDraft(row) {
  return {
    clientId: row.hash,
    serverHash: row.hash,
    form: {
      company_name: row.company_name ?? "",
      website_url: row.website_url ?? "",
      core_offering: row.core_offering ?? "",
      icp: row.icp ?? "",
      custom_context: row.custom_context ?? "",
      brand_context:
        row.brand_context && typeof row.brand_context === "object" ? { ...row.brand_context } : {},
    },
  };
}

const Onboarding = ({ authenticated }) => {
  const navigate = useNavigate();
  const { stepSlug } = useParams();
  const step = onboardingStepFromSlug(stepSlug || "");
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mailboxReturnError, setMailboxReturnError] = useState("");
  const [mailboxOAuthReturnReason, setMailboxOAuthReturnReason] = useState("");
  const [addBrandRequestSignal, setAddBrandRequestSignal] = useState(0);
  const [brandSubstep, setBrandSubstep] = useState("url");
  const [brandDrafts, setBrandDrafts] = useState([]);
  const [activeClientId, setActiveClientId] = useState("");
  const activeClientIdRef = useRef("");
  /** When `current === 1` we already applied `brandsListQuery` into local drafts for this stay on step 1. */
  const prevBrandStepRef = useRef(null);
  const [formState, setFormState] = useState({
    tone: "helpful_peer",
    reply_length: "medium",
    reply_custom_context: "",
    email_retention_policy: EMAIL_RETENTION_COLD_INBOUND_ONLY,
  });

  useEffect(() => {
    activeClientIdRef.current = activeClientId;
  }, [activeClientId]);

  const brandsListQuery = useQuery({
    queryKey: ["brand-profiles-all"],
    queryFn: brandProfileService.listAll,
    enabled: authenticated,
  });

  useLayoutEffect(() => {
    if (!authenticated) return;
    if (step !== 1) {
      prevBrandStepRef.current = step;
      return;
    }
    if (!brandsListQuery.isFetched) return;
    if (prevBrandStepRef.current === 1) return;

    if (brandsListQuery.isError) {
      const d = newLocalDraft();
      setBrandDrafts([d]);
      setActiveClientId(d.clientId);
      setBrandSubstep("url");
      prevBrandStepRef.current = 1;
      return;
    }

    const data = brandsListQuery.data;
    if (data == null) return;

    const results = data.results ?? [];
    if (results.length === 0) {
      const d = newLocalDraft();
      setBrandDrafts([d]);
      setActiveClientId(d.clientId);
      setBrandSubstep("url");
    } else {
      const drafts = results.map(brandRowToDraft);
      setBrandDrafts(drafts);
      setActiveClientId(drafts[0].clientId);
      if (results.length === 1) {
        setBrandSubstep(brandHasPitchContent(results[0]) ? "details" : "url");
      } else {
        setBrandSubstep("details");
      }
    }
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(MAILBOX_JUST_CONNECTED_KEY)) {
      sessionStorage.removeItem(MAILBOX_JUST_CONNECTED_KEY);
    }
    prevBrandStepRef.current = 1;
  }, [
    authenticated,
    step,
    brandsListQuery.isFetched,
    brandsListQuery.isError,
    brandsListQuery.data,
  ]);

  const userPrefsQuery = useQuery({
    queryKey: userProfileQueryKey,
    queryFn: userProfileService.get,
    enabled: authenticated,
  });

  useEffect(() => {
    const d = userPrefsQuery.data;
    if (!d) return;
    setFormState((prev) => ({
      ...prev,
      tone: normalizeToneValue(d.tone || prev.tone),
      reply_length:
        d.reply_length != null && d.reply_length !== "" ? String(d.reply_length) : prev.reply_length,
      reply_custom_context: d.reply_custom_context || "",
      email_retention_policy: normalizeEmailRetentionValue(
        d.email_retention_policy ?? prev.email_retention_policy
      ),
    }));
  }, [userPrefsQuery.data]);

  useLayoutEffect(() => {
    if (!authenticated) return;
    const mailbox = searchParams.get("mailbox");
    if (!mailbox) return;
    const reason = searchParams.get("reason");
    const mailboxHashParam = searchParams.get("mailbox_hash") || "";
    const next = new URLSearchParams(searchParams);
    next.delete("mailbox");
    next.delete("reason");
    next.delete("email");
    next.delete("mailbox_hash");
    setSearchParams(next, { replace: true });

    if (mailbox === "connected") {
      if (mailboxHashParam) {
        persistMailboxHash(mailboxHashParam);
      }
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(MAILBOX_JUST_CONNECTED_KEY, "1");
      }
      queryClient.invalidateQueries({ queryKey: mailboxesQueryKey });
      setMailboxReturnError("");
      setMailboxOAuthReturnReason("");
    } else if (mailbox === "error") {
      const r = (reason || "").trim();
      setMailboxOAuthReturnReason(r);
      setMailboxReturnError(mailboxOAuthErrorMessage(reason));
    }
  }, [authenticated, searchParams, setSearchParams, queryClient]);

  const mailboxListQuery = useQuery({
    queryKey: mailboxesQueryKey,
    queryFn: mailboxService.list,
    enabled: authenticated,
  });
  const mailboxResults = mailboxListQuery.data?.results || [];
  const primaryMailbox = defaultMailbox(mailboxResults);
  const connectedMailboxes = mailboxResults.filter((m) => m.connected);

  const saveBrandsBulkMutation = useMutation({
    mutationFn: (brands) => brandProfileService.bulkUpsert({ brands }),
  });

  const removeBrandMutation = useMutation({
    mutationFn: (hash) => brandProfileService.remove(hash),
    onSuccess: (_data, hash) => {
      queryClient.invalidateQueries({ queryKey: ["brand-profiles-all"] });
      queryClient.invalidateQueries({ queryKey: ["brand-profile"] });
      setBrandDrafts((prev) => {
        const next = prev.filter((d) => d.serverHash !== hash);
        const removed = prev.find((d) => d.serverHash === hash);
        if (removed && activeClientIdRef.current === removed.clientId) {
          const idx = prev.findIndex((d) => d.clientId === removed.clientId);
          const neighbor = next[Math.max(0, idx - 1)] || next[0];
          if (neighbor) setActiveClientId(neighbor.clientId);
        }
        return next;
      });
    },
  });

  const saveUserMutation = useMutation({
    mutationFn: (payload) => userProfileService.update(payload),
  });

  const emailRetentionMutation = useMutation({
    mutationFn: (email_retention_policy) => userProfileService.update({ email_retention_policy }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userProfileQueryKey });
    },
  });

  const brandPreviewMutation = useMutation({
    mutationFn: (websiteUrl) => brandProfileService.prefillFromWebsite(websiteUrl),
    onSuccess: (data) => {
      trackEvent("onboarding_brand_prefill_succeeded");
      const p = data?.prefill || {};
      const id = activeClientIdRef.current;
      setBrandDrafts((prev) =>
        prev.map((d) =>
          d.clientId !== id
            ? d
            : {
                ...d,
                form: {
                  ...d.form,
                  website_url: data?.website_url || d.form.website_url,
                  company_name: p.company_name ?? "",
                  core_offering: p.core_offering ?? "",
                  icp: p.icp ?? "",
                  brand_context:
                    data?.webpage_content && String(data.webpage_content).trim()
                      ? { ...d.form.brand_context, webpage_content: data.webpage_content }
                      : d.form.brand_context,
                },
              }
        )
      );
      setBrandSubstep("details");
    },
    onError: (err) => {
      trackEvent("onboarding_brand_prefill_failed");
      const body = err?.response?.data;
      const canonical = body?.website_url;
      const id = activeClientIdRef.current;
      setBrandDrafts((prev) =>
        prev.map((d) =>
          d.clientId !== id
            ? d
            : {
                ...d,
                form: {
                  ...d.form,
                  ...(canonical ? { website_url: canonical } : {}),
                  company_name: "",
                  core_offering: "",
                  icp: "",
                },
              }
        )
      );
      setBrandSubstep("details");
    },
  });

  const mailboxStartMutation = useMutation({
    mutationFn: () => mailboxService.start({ return_path: ONBOARDING_MAILBOX_RETURN_PATH }),
    onSuccess: (data) => {
      window.location.href = data.auth_url;
    },
  });

  const mailboxDisconnectMutation = useMutation({
    mutationFn: (hash) => mailboxService.disconnect(hash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxesQueryKey });
    },
  });

  const runMailboxOAuth = () => {
    trackEvent("onboarding_mailbox_connect_clicked");
    mailboxStartMutation.reset();
    setMailboxReturnError("");
    setMailboxOAuthReturnReason("");
    mailboxStartMutation.mutate();
  };

  const handleDisconnectMailbox = (hash) => {
    const ok = window.confirm("Are you sure? You'll lose any leads from this mailbox.");
    if (!ok) return;
    mailboxDisconnectMutation.mutate(hash);
  };

  const loading = brandsListQuery.isLoading || userPrefsQuery.isLoading;

  useEffect(() => {
    if (!authenticated || step == null) return;
    trackEvent("onboarding_step_viewed", { step, brand_substep: brandSubstep });
  }, [authenticated, step, brandSubstep]);

  const errorMessage = useMemo(() => {
    if (!authenticated) return "";
    if (brandsListQuery.isError) return getApiErrorMessage(brandsListQuery.error);
    if (userPrefsQuery.isError) return getApiErrorMessage(userPrefsQuery.error);
    if (saveBrandsBulkMutation.isError) return getApiErrorMessage(saveBrandsBulkMutation.error);
    if (saveUserMutation.isError) return getApiErrorMessage(saveUserMutation.error);
    if (emailRetentionMutation.isError) return getApiErrorMessage(emailRetentionMutation.error);
    if (brandPreviewMutation.isError) return getApiErrorMessage(brandPreviewMutation.error);
    if (removeBrandMutation.isError) return getApiErrorMessage(removeBrandMutation.error);
    if (mailboxStartMutation.isError) return getApiErrorMessage(mailboxStartMutation.error);
    if (mailboxDisconnectMutation.isError) return getApiErrorMessage(mailboxDisconnectMutation.error);
    return "";
  }, [
    authenticated,
    brandsListQuery.isError,
    brandsListQuery.error,
    userPrefsQuery.isError,
    userPrefsQuery.error,
    saveBrandsBulkMutation.isError,
    saveBrandsBulkMutation.error,
    saveUserMutation.isError,
    saveUserMutation.error,
    emailRetentionMutation.isError,
    emailRetentionMutation.error,
    brandPreviewMutation.isError,
    brandPreviewMutation.error,
    removeBrandMutation.isError,
    removeBrandMutation.error,
    mailboxStartMutation.isError,
    mailboxStartMutation.error,
    mailboxDisconnectMutation.isError,
    mailboxDisconnectMutation.error,
  ]);

  const updateActiveForm = (patch) => {
    const id = activeClientIdRef.current;
    setBrandDrafts((prev) =>
      prev.map((d) => (d.clientId !== id ? d : { ...d, form: { ...d.form, ...patch } }))
    );
  };

  const handleAppendBrandDraft = (form) => {
    const d = { ...newLocalDraft(), form: { ...emptyBrandForm(), ...form } };
    setBrandDrafts((prev) => [...prev, d]);
    setActiveClientId(d.clientId);
    trackEvent("onboarding_brand_added_during_icp");
  };

  const handleRemoveBrand = (clientId) => {
    if (brandDrafts.length <= 1) return;
    const draft = brandDrafts.find((d) => d.clientId === clientId);
    if (!draft) return;
    if (draft.serverHash) {
      removeBrandMutation.mutate(draft.serverHash);
      return;
    }
    setBrandDrafts((prev) => {
      const next = prev.filter((d) => d.clientId !== clientId);
      if (activeClientId === clientId) {
        const idx = prev.findIndex((d) => d.clientId === clientId);
        const neighbor = next[Math.max(0, idx - 1)] || next[0];
        if (neighbor) setActiveClientId(neighbor.clientId);
      }
      return next;
    });
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-theme-main flex items-center justify-center font-mono text-[12px] text-theme-muted">
        Please sign in to continue onboarding.
      </div>
    );
  }

  if (step == null) {
    return <Navigate to={ONBOARDING_FIRST_PATH} replace />;
  }

  const nextStep = () => {
    const lastStep = ONBOARDING_STEP_SLUGS.length;
    const target = Math.min(step + 1, lastStep);
    if (step === 1 && brandSubstep === "url") return;
    if (step === 1) {
      if (brandSubstep === "details" && !brandDrafts.every((d) => brandOnboardingDetailsComplete(d.form))) {
        return;
      }
      const brands = brandDrafts.map((d) => ({
        ...(d.serverHash ? { hash: d.serverHash } : {}),
        company_name: d.form.company_name,
        website_url: d.form.website_url,
        core_offering: d.form.core_offering,
        icp: d.form.icp,
        custom_context: d.form.custom_context,
        brand_context: d.form.brand_context && typeof d.form.brand_context === "object" ? d.form.brand_context : {},
      }));
      saveBrandsBulkMutation.reset();
      saveBrandsBulkMutation.mutate(brands, {
        onSuccess: () => {
          trackEvent("onboarding_step_completed", { step });
          queryClient.invalidateQueries({ queryKey: ["brand-profiles-all"] });
          queryClient.invalidateQueries({ queryKey: ["brand-profile"] });
          navigate(onboardingPathForStep(target));
          window.scrollTo(0, 0);
        },
      });
      return;
    }
    if (step === 2) {
      saveUserMutation.reset();
      saveUserMutation.mutate(
        {
          tone: formState.tone,
          reply_length: formState.reply_length,
          reply_custom_context: formState.reply_custom_context,
        },
        {
          onSuccess: () => {
            trackEvent("onboarding_step_completed", { step });
            queryClient.invalidateQueries({ queryKey: userProfileQueryKey });
            navigate(onboardingPathForStep(target));
            window.scrollTo(0, 0);
          },
        }
      );
      return;
    }
  };

  const prevStep = () => {
    const soleLocal =
      brandDrafts.length === 1 && brandDrafts[0] && !brandDrafts[0].serverHash ? brandDrafts[0] : null;
    if (step === 1 && brandSubstep === "details" && soleLocal) {
      saveBrandsBulkMutation.reset();
      brandPreviewMutation.reset();
      setBrandSubstep("url");
      return;
    }
    saveBrandsBulkMutation.reset();
    saveUserMutation.reset();
    emailRetentionMutation.reset();
    brandPreviewMutation.reset();
    removeBrandMutation.reset();
    if (step <= 1) return;
    if (step === 2) {
      setBrandSubstep("details");
      queryClient.invalidateQueries({ queryKey: ["brand-profiles-all"] });
    }
    navigate(onboardingPathForStep(step - 1));
  };

  const finishOnboarding = () => {
    saveUserMutation.reset();
    saveUserMutation.mutate(
      {
        email_retention_policy: formState.email_retention_policy,
        setup_complete: true,
      },
      {
        onSuccess: () => {
          trackEvent("onboarding_completed");
          queryClient.invalidateQueries({ queryKey: userProfileQueryKey });
          navigate("/audit/?openRunning=1");
        },
      }
    );
  };

  const nextButtonLoading =
    (step === 1 && brandSubstep === "details" && saveBrandsBulkMutation.isPending) ||
    (step === 2 && saveUserMutation.isPending);

  const activeDraft = brandDrafts.find((d) => d.clientId === activeClientId);

  /** First extra brand only after the current profile passes ICP required fields, or when multiple brands already exist. */
  const showAddAnotherFooter =
    brandDrafts.length > 1 ||
    (brandSubstep === "details" && activeDraft && brandOnboardingDetailsComplete(activeDraft.form));

  const handleBrandUrlContinue = () => {
    const raw = activeDraft?.form.website_url.trim() || "";
    if (!raw) return;
    trackEvent("onboarding_brand_prefill_requested");
    brandPreviewMutation.reset();
    brandPreviewMutation.mutate(raw);
  };

  const handleNoBrandWebsite = () => {
    trackEvent("onboarding_brand_prefill_skipped");
    setBrandSubstep("details");
  };

  const handleEmailRetentionChange = (email_retention_policy) => {
    setFormState((prev) => ({ ...prev, email_retention_policy }));
    emailRetentionMutation.mutate(email_retention_policy);
  };

  const footerShowBack = !(step === 1 && brandSubstep === "url");
  const footerShowNext = !(step === 1 && brandSubstep === "url");
  const brandDetailsNextBlocked =
    step === 1 &&
    brandSubstep === "details" &&
    (!brandDrafts.length || !brandDrafts.every((d) => brandOnboardingDetailsComplete(d.form)));

  const onLastOnboardingStep = step === ONBOARDING_STEP_SLUGS.length;
  const completeSetupBlocked = onLastOnboardingStep && !primaryMailbox;

  const alert = errorMessage || mailboxReturnError;
  const alertTone =
    !errorMessage &&
    mailboxReturnError &&
    mailboxOAuthErrorIsAccountInUse(mailboxOAuthReturnReason)
      ? "danger"
      : "default";
  const footerRight =
    step < ONBOARDING_STEP_SLUGS.length ? (
      step === 1 && brandSubstep === "details" ? (
        <>
          {showAddAnotherFooter ? (
            <Button variant="ghost" size="sm" onClick={() => setAddBrandRequestSignal((n) => n + 1)}>
              Add another
            </Button>
          ) : null}
          <Button
            onClick={nextStep}
            loading={nextButtonLoading}
            disabled={brandDetailsNextBlocked}
            title={
              brandDetailsNextBlocked
                ? "Add company name, what you sell, and your ideal customer profile to continue."
                : undefined
            }
          >
            Next
          </Button>
        </>
      ) : step >= 2 ? (
        <Button onClick={nextStep} loading={nextButtonLoading}>
          Next
        </Button>
      ) : null
    ) : (
      <Button
        onClick={finishOnboarding}
        loading={saveUserMutation.isPending}
        disabled={completeSetupBlocked}
        title={
          completeSetupBlocked ? "Connect a Gmail inbox with the button above to complete setup." : undefined
        }
      >
        Complete Setup
      </Button>
    );

  let stepBody = null;
  if (!loading && step === 1 && activeDraft) {
    stepBody = (
      <OnboardingBrandStep
        brandDrafts={brandDrafts}
        activeClientId={activeClientId}
        onSelectBrand={setActiveClientId}
        activeForm={activeDraft.form}
        updateActiveForm={updateActiveForm}
        onRemoveBrand={handleRemoveBrand}
        removeBrandPending={removeBrandMutation.isPending}
        onAppendBrandDraft={handleAppendBrandDraft}
        addBrandRequestSignal={addBrandRequestSignal}
        brandSubstep={brandSubstep}
        brandPreviewMutation={brandPreviewMutation}
        onBrandUrlContinue={handleBrandUrlContinue}
        onNoBrandWebsite={handleNoBrandWebsite}
      />
    );
  } else if (!loading && step === 2) {
    stepBody = <OnboardingReplyStyleStep formState={formState} setFormState={setFormState} />;
  } else if (!loading && step === 3) {
    stepBody = (
      <OnboardingGmailStep
        formState={formState}
        connectedMailboxes={connectedMailboxes}
        onContinueToGoogle={runMailboxOAuth}
        mailboxOAuthLoading={mailboxStartMutation.isPending}
        onEmailRetentionChange={handleEmailRetentionChange}
        retentionPersistPending={emailRetentionMutation.isPending}
        onDisconnectMailbox={handleDisconnectMailbox}
        disconnectMailboxPendingHash={
          mailboxDisconnectMutation.isPending ? mailboxDisconnectMutation.variables : null
        }
      />
    );
  }

  return (
    <OnboardingShell
      alert={alert}
      alertTone={alertTone}
      loading={loading}
      step={step}
      footerShowBack={footerShowBack}
      onFooterBack={prevStep}
      footerShowNext={footerShowNext}
      footerRight={footerRight}
    >
      {stepBody}
    </OnboardingShell>
  );
};

export default Onboarding;
